import React, { useEffect, useState } from 'react'
import { Table, Button, Modal, Spin, Alert } from 'antd'
import { useSelector } from 'react-redux'
import { API } from 'src/utils/interceptor'
import Loading from 'src/components/styled/Loading'
import { stickyActionsColumn } from 'src/components/styled/horizontalScroll'
import styled from 'styled-components/macro'
import { useSupplierInvitesCounts } from 'src/context/SupplierInvitesCountsContext'
import { formatInviteDate } from 'src/utils/supplierInvitesStorage'
import { PENDING_INVITES_GUIDANCE } from 'src/utils/supplierInvitesAdminCopy'
import { showErrorToast, showSuccessToast } from 'src/utils/messageToast'

const AdminPendingInvites = () => {
  const username = useSelector(state => state.auth.cognito.username)
  const { refreshCounts } = useSupplierInvitesCounts()
  const [invites, setInvites] = useState([])
  const [loading, setLoading] = useState(true)
  const [completingInviteId, setCompletingInviteId] = useState(null)

  useEffect(() => {
    if (username) {
      fetchInvites()
    }
  }, [username])

  const fetchInvites = async () => {
    if (!username) {
      showErrorToast('Username not available')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const response = await API.get('api2', `suppliers/invites`, {
        queryStringParameters: { username },
      })
      if (response.success) {
        setInvites(response.data || [])
      } else {
        showErrorToast(response.message || 'Failed to load pending invites')
      }
    } catch (error) {
      console.error('Error fetching invites:', error)
      showErrorToast('Failed to load pending invites')
    } finally {
      setLoading(false)
    }
  }

  const handleCompleteInvite = async (inviteId, supplierName) => {
    if (completingInviteId) return

    setCompletingInviteId(inviteId)
    try {
      const response = await API.delete(
        'api2',
        `suppliers/invites/${inviteId}`,
        {
          queryStringParameters: { username },
        },
      )
      if (response.success) {
        if (response.notificationSent) {
          showSuccessToast(
            supplierName
              ? `${supplierName} is ready — we've emailed the team.`
              : "Supplier is ready — we've emailed the team.",
          )
        } else {
          showSuccessToast(
            supplierName
              ? `${supplierName} marked as resolved.`
              : 'Invite marked as resolved.',
          )
        }
        setInvites(prev => prev.filter(inv => inv._id !== inviteId))
        refreshCounts()
      } else {
        showErrorToast(response.message || 'Failed to mark invite as resolved')
      }
    } catch (error) {
      console.error('Error completing invite:', error)
      showErrorToast('Failed to mark invite as resolved')
    } finally {
      setCompletingInviteId(null)
    }
  }

  const confirmResolveInvite = record => {
    Modal.confirm({
      title: 'Mark as resolved?',
      content: (
        <>
          Confirm <strong>{record.supplierName}</strong> has been added for{' '}
          <strong>{record.venue}</strong>. We&apos;ll email the venue team so
          they can start ordering.
        </>
      ),
      okText: 'Mark as resolved',
      cancelText: 'Cancel',
      onOk: () => handleCompleteInvite(record._id, record.supplierName),
    })
  }

  const columns = [
    {
      title: 'Venue',
      dataIndex: 'venue',
      key: 'venue',
      className: 'restrictShrink',
      sorter: (a, b) =>
        String(a.venue).trim().localeCompare(String(b.venue).trim()),
    },
    {
      title: 'Supplier Name',
      dataIndex: 'supplierName',
      key: 'supplierName',
      className: 'restrictShrink',
      sorter: (a, b) =>
        String(a.supplierName)
          .trim()
          .localeCompare(String(b.supplierName).trim()),
    },
    {
      title: 'Email',
      dataIndex: 'supplierEmail',
      key: 'supplierEmail',
      className: 'restrictShrink',
    },
    {
      title: 'Account Number',
      dataIndex: 'accountNumber',
      key: 'accountNumber',
      className: 'restrictShrink',
      render: text => text || '-',
    },
    {
      title: 'Date/Time Requested',
      dataIndex: 'requestedAt',
      key: 'requestedAt',
      className: 'restrictShrink',
      render: date => formatInviteDate(date),
      sorter: (a, b) => new Date(a.requestedAt) - new Date(b.requestedAt),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Action',
      key: 'action',
      className: 'restrictShrink sticky-actions-col',
      onCell: () => ({ className: 'sticky-actions-cell' }),
      render: (_, record) => {
        const isCompleting = completingInviteId === record._id
        return (
          <ActionCell>
            {isCompleting ? (
              <Spin size='small' />
            ) : (
              <Button
                type='primary'
                size='small'
                disabled={Boolean(completingInviteId)}
                onClick={() => confirmResolveInvite(record)}
              >
                Mark as resolved
              </Button>
            )}
          </ActionCell>
        )
      },
    },
  ]

  if (loading) {
    return (
      <TabContent>
        <Loading />
      </TabContent>
    )
  }

  return (
    <TabContent>
      <GuidanceAlert type='info' showIcon message={PENDING_INVITES_GUIDANCE} />
      {invites.length === 0 ? (
        <EmptyState>
          <EmptyStateText>
            All caught up — no pending supplier invites.
          </EmptyStateText>
        </EmptyState>
      ) : (
        <Table
          dataSource={invites.map(invite => ({
            ...invite,
            key: invite._id,
          }))}
          columns={columns}
          pagination={false}
          scroll={{ x: true }}
        />
      )}
    </TabContent>
  )
}

const TabContent = styled.div`
  ${stickyActionsColumn}
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const GuidanceAlert = styled(Alert)`
  margin-bottom: 0;
`

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  background-color: #fafafa;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
`

const EmptyStateText = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
`

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 32px;
`

export default AdminPendingInvites
